import { aiAttribution, safePageReferrer, safePublicPageLocation } from '~/utils/analyticsPrivacy'

type Gtag = (command: string, target: string | Date, params?: Record<string, unknown>) => void

export default defineNuxtPlugin((nuxtApp) => {
  const measurementId = String(useRuntimeConfig().public.gaMeasurementId || '').trim()
  if (!/^G-[A-Z0-9]+$/i.test(measurementId)) return

  const { consent } = useCookieConsent()
  const w = window as typeof window & { dataLayer?: unknown[][], gtag?: Gtag }
  let loaded = false
  let lastTrackedLocation: string | undefined

  function sendPageView() {
    if (!loaded || !consent.value?.analytics || typeof w.gtag !== 'function') return
    const pageLocation = safePublicPageLocation(window.location.href)
    if (!pageLocation || pageLocation === lastTrackedLocation) return
    const source = aiAttribution(window.location.href, document.referrer)
    w.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: pageLocation,
      page_referrer: safePageReferrer(document.referrer, window.location.origin),
      ai_referrer_source: source,
    })
    lastTrackedLocation = pageLocation
  }

  function loadAnalytics() {
    if (loaded || !consent.value?.analytics) return
    w.dataLayer = w.dataLayer || []
    w.gtag = function gtag(...args: unknown[]) { w.dataLayer!.push(args) } as Gtag
    w.gtag('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: consent.value.marketing ? 'granted' : 'denied',
      ad_user_data: consent.value.marketing ? 'granted' : 'denied',
      ad_personalization: consent.value.marketing ? 'granted' : 'denied',
    })
    w.gtag('js', new Date())
    w.gtag('config', measurementId, {
      send_page_view: false,
      anonymize_ip: true,
      cookie_domain: window.location.hostname,
    })

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    script.dataset.midigiAnalytics = 'true'
    document.head.appendChild(script)
    loaded = true
    queueMicrotask(sendPageView)
  }

  watch(consent, (state) => {
    if (state?.analytics) {
      loadAnalytics()
      return
    }
    if (loaded && typeof w.gtag === 'function') {
      w.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      })
    }
  }, { immediate: true })

  nuxtApp.hook('page:finish', sendPageView)
})
